import {Component, inject, output, OnInit, ViewChild, ElementRef} from '@angular/core';
import {FormBuilder, FormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {SliderManagementModel} from "../models/slider-management.model";
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatButtonModule} from '@angular/material/button';
import {MatNativeDateModule} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {SliderManagementService} from "../services/slider-management.service";
import {provideNativeDateAdapter} from '@angular/material/core';

@Component({
  selector: 'app-slider-form',
  providers: [provideNativeDateAdapter()],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    MatNativeDateModule,
    MatDialogModule
  ],
  templateUrl: './slider-form.component.html',
  styleUrl: './slider-form.component.scss',
})
export class SliderFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private data = inject<{ slide?: SliderManagementModel }>(MAT_DIALOG_DATA);
  private slideService = inject(SliderManagementService);
  private dialogRef = inject(MatDialogRef<SliderFormComponent>);

  slideImage: any;
  @ViewChild('imageSlide') imageSlide!: ElementRef;

  slideForm!: FormGroup;
  isEditMode = false;

  slideSubmitted = output<SliderManagementModel>();
  cancelled = output<void>();
  imageSelected = output<File>();

  private initializeSlideForm() {
    this.slideForm = this.formBuilder.group({
      slideId: [null],
      title: ['', [Validators.required]],
      link: [''],
      start: ['', Validators.required],
      end: ['', Validators.required],
      order: [0, [Validators.required, Validators.min(0)]],
      image: ['', Validators.required],
      imagePath: ['']
    });
  }

  ngOnInit() {
    this.initializeSlideForm();

    if (this.data?.slide) {
      this.isEditMode = true;
      this.updateSlideForm(this.data.slide);
    }
  }

  private updateSlideForm(slide: SliderManagementModel) {
    this.slideForm.get('image')?.clearValidators();
    this.slideForm.get('image')?.updateValueAndValidity();

    this.slideForm.patchValue({
      slideId: slide.slideId,
      title: slide.title,
      link: slide.link,
      start: slide.start ? new Date(slide.start) : null,
      end: slide.end ? new Date(slide.end) : null,
      order: slide.order,
      imagePath: slide.imagePath
    });
  }

  async onSubmit() {
    if (this.slideForm.valid) {
      const formValue = this.slideForm.value;

      if (formValue.start.toString().indexOf('GMT') > -1) {
        formValue.start = formValue.start.toISOString().split('T')[0] + 'T00:00:01';
      }
      if (formValue.end.toString().indexOf('GMT') > -1) {
        formValue.end = formValue.end.toISOString().split('T')[0] + 'T23:59:59';
      }

      if (this.slideImage) {
        const imagePath = 'slider/imgs/' + this.slideImage.name;
        await this.slideService.uploadImage(this.slideImage, imagePath);
        formValue.imagePath = imagePath;
      }

      try {
        if (this.isEditMode) {
          await this.slideService.updateSlide(formValue);
        } else {
          await this.slideService.addSlide(formValue);
        }
        this.dialogRef.close(true);
      } catch (error) {
        console.error('Error saving slide:', error);
      }
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  isFormValid(): boolean {
    return this.slideForm?.valid ?? false;
  }

  imagePath(event: any) {
    this.slideImage = event.target.files[0];
  }
}
